/**
 * Navigation Drawer
 * Capability-gated grouped navigation (Iteration 12)
 * 
 * Groups shown are determined by current customer's capability:
 *   manual → Manual Counts group + Projects
 *   beacon → Beacon Tracking group + Projects
 *   hybrid → both groups
 *   staff/admin "all customers" → union of all groups (LAT-07)
 * 
 * Switching customer within same session removes inapplicable groups
 * without reload (LAT-07).
 * 
 * Drawer pinning: pin toggle persists in localStorage; survives reload (LAT-09).
 * Pinned drawer occupies horizontal layout; unpinned does not (LAT-09).
 * 
 * Design: 280px, bg colors.surface, footer profile card
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Button,
  Divider,
  Box,
  Typography,
  Avatar,
  Collapse,
  IconButton,
  Skeleton,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Map as MapIcon,
  FormatListBulleted as ListIcon,
  Inventory2 as MaterialsIcon,
  People as PeopleIcon,
  AccountCircle as AccountIcon,
  Settings as SettingsIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  Layers as LayersIcon,
  Numbers as NumbersIcon,
  SwapHoriz as SwapHorizIcon,
  Logout as LogoutIcon,
  KeyboardArrowDown as ChevronDownIcon,
  KeyboardArrowUp as ChevronUpIcon,
  PushPin as PushPinIcon,
  PushPinOutlined as PushPinOutlinedIcon,
} from '@mui/icons-material';

import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { signOut, memberOf } from '../../store/slices/authSlice';
import { setDrawerNavOpen } from '../../store/slices/uiSlice';
import CustomerSelector from './CustomerSelector';
import { colors } from '../../theme';
import { canManageManualAssets } from '../../hooks/useCanManageManualAssets';

const DRAWER_WIDTH = 280;
const PIN_STORAGE_KEY = 'assetrac-nav-pinned';

// Icon map for navigation destinations (ASS-65: rebuilt to Mark's example)
const ICON_MAP = {
  Map: <MapIcon />,
  List: <ListIcon />,
  Materials: <MaterialsIcon />,
  People: <PeopleIcon />,
  Account: <AccountIcon />,
  Settings: <SettingsIcon />,
  Business: <BusinessIcon />,
  Category: <CategoryIcon />,
  Layers: <LayersIcon />, // AST-114 NAV-3: distinct from Global asset types' Category icon
  Numbers: <NumbersIcon />, // AST-126 MC-2: a count/numbers icon
  SwapHoriz: <SwapHorizIcon />,
};

const NavDrawer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const { drawerNavOpen } = useAppSelector((state) => state.ui);
  const { userAttributes, userGroups } = useAppSelector((state) => state.auth);
  const { loading: customerLoading } = useAppSelector((state) => state.customer);
  // AST-114: re-evaluated when the drawer's Customer selector changes (NAV-8).
  const activeCustomerId = useAppSelector((state) => state.customer.activeCustomer?.id);
  const showManualAssets = canManageManualAssets(userGroups, activeCustomerId);

  const [isAdmin, setIsAdmin] = useState(false);
  const [profileExpanded, setProfileExpanded] = useState(false);
  const [isPinned, setIsPinned] = useState(() => {
    try {
      return localStorage.getItem(PIN_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const checkAdmin = async () => {
      const result = await dispatch(memberOf('admins'));
      setIsAdmin(result);
    };
    checkAdmin();
  }, [dispatch, userGroups]);

  const handleSignOut = async () => {
    await dispatch(signOut());
    navigate('/');
  };

  const handleNavClick = (path) => {
    if (!isPinned) {
      dispatch(setDrawerNavOpen(false));
    }
    navigate(path);
  };

  const handleTogglePin = () => {
    const newPinned = !isPinned;
    setIsPinned(newPinned);
    try {
      localStorage.setItem(PIN_STORAGE_KEY, String(newPinned));
    } catch {
      // localStorage unavailable
    }
    if (newPinned) {
      dispatch(setDrawerNavOpen(true));
    }
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // ASS-65: rebuilt to Mark's 08-19 sketch IA.
  // As Customer · PROJECTS(MAP, LIST) · MATERIALS · ADMINISTRATION(USERS, MY ACCOUNT)
  // · MATRAC SETTINGS (EMPLOYEE badge, employees-only label) · SIGN OUT.
  //
  // MATERIALS is a dead placeholder — in Mark's IA, deferred off Friday's build.
  // It must not be given a live destination.
  //
  // MATRAC SETTINGS is the super-admin section (was ASSETTRAC). Gated on isAdmin.
  // ABSENT FROM DOM for non-admins (security-relevant, per R-09's negative-half
  // falsifier).
  const navGroups = [];

  // PROJECTS — always shown.
  navGroups.push({
    name: 'Projects',
    destinations: [
      { label: 'Map', path: '/dashboard', icon: 'Map' },
      { label: 'List', path: '/projects', icon: 'List' },
    ],
  });

  // ASSETS -- AST-126 MC-1..3: directly after PROJECTS, shown to everyone who
  // sees PROJECTS (i.e. always). The page and the API decide what's in it.
  navGroups.push({
    name: 'Assets',
    destinations: [
      { label: 'Manual Asset Count', path: '/assets/manual-counts', icon: 'Numbers' },
    ],
  });

  // MATERIALS — dead placeholder. In Mark's IA; deferred. No live destination.
  navGroups.push({
    name: 'Materials',
    destinations: [
      { label: 'Materials', path: null, icon: 'Materials', disabled: true },
    ],
  });

  // ADMINISTRATION — always shown.
  // AST-114: Manual Assets first (NAV-2), only for staff or a Customer Admin
  // of the selected customer (NAV-7) -- ABSENT from the DOM otherwise, same
  // as Matrac Settings. The route and API re-check (NAV-12).
  navGroups.push({
    name: 'Administration',
    destinations: [
      ...(showManualAssets ? [{ label: 'Manual Assets', path: '/admin/manual-assets', icon: 'Layers' }] : []),
      { label: 'Users', path: '/users', icon: 'People' },
      { label: 'My Account', path: '/account', icon: 'Account' },
    ],
  });

  // MATRAC SETTINGS — super-admin only. ABSENT FROM DOM for non-admins.
  if (isAdmin) {
    navGroups.push({
      name: 'Matrac Settings',
      badge: 'EMPLOYEE',
      badgeLabel: 'Employees only',
      destinations: [
        { label: 'Customers', path: '/admin/customers', icon: 'Business' },
        { label: 'AssetTrac users', path: '/admin/users', icon: 'People' },
        { label: 'Global asset types', path: '/admin/asset-types', icon: 'Category' },
        { label: 'Reassign assets', path: '/admin/reassignment', icon: 'SwapHoriz' },
      ],
    });
  }

  const userName = userAttributes?.name || userAttributes?.email || 'User';
  const userRole = isAdmin ? 'Administrator' : 'Member';
  const userInitials = userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        py: 2,
      }}
    >
      {/* Pin toggle + Customer Selector */}
      <Box sx={{ px: 2, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {isAdmin && (
          <Box sx={{ flex: 1, mr: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '0.65rem',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                display: 'block',
                mb: 1,
              }}
            >
              Customer
            </Typography>
            <CustomerSelector />
          </Box>
        )}
        <Tooltip title={isPinned ? 'Unpin drawer' : 'Pin drawer'}>
          <IconButton
            size="small"
            onClick={handleTogglePin}
            sx={{ color: isPinned ? theme.palette.text.primary : theme.palette.text.secondary, mt: isAdmin ? 2 : 0 }}
          >
            {isPinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Loading state */}
      {customerLoading ? (
        <Box sx={{ px: 2 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rectangular" height={36} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </Box>
      ) : (
        /* Capability-gated navigation groups */
        navGroups.map((group, groupIdx) => (
          <Box key={group.name}>
            {groupIdx > 0 && (
              <Divider sx={{ borderColor: theme.palette.divider, my: 1.5, mx: 2 }} />
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, py: 0.5 }}>
              <Typography
                variant="overline"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.65rem',
                  letterSpacing: '1px',
                }}
              >
                {group.name}
              </Typography>
              {group.badge && (
                <Typography
                  variant="caption"
                  sx={{
                    backgroundColor: colors.stateSelected,
                    color: theme.palette.text.secondary,
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 0.5,
                    fontSize: '0.6rem',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                    lineHeight: 1,
                  }}
                >
                  {group.badge}
                </Typography>
              )}
            </Box>
            {group.badgeLabel && (
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.secondary, fontSize: '0.6rem', px: 3, display: 'block', mb: 0.5 }}
              >
                {group.badgeLabel}
              </Typography>
            )}
            <List sx={{ px: 1, py: 0 }}>
              {group.destinations.map((dest) => (
                <ListItem key={dest.path || dest.label} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={dest.disabled ? undefined : () => handleNavClick(dest.path)}
                    disabled={!!dest.disabled}
                    selected={!dest.disabled && isActive(dest.path)}
                    sx={{
                      borderRadius: 1,
                      py: 1,
                      color: isActive(dest.path) ? theme.palette.text.primary : colors.stateTextDim,
                      '&.Mui-selected': {
                        backgroundColor: colors.stateSelected,
                      },
                      '&.Mui-selected:hover': {
                        backgroundColor: colors.stateSelectedHover,
                      },
                      '&:hover': {
                        backgroundColor: colors.stateHover,
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
                      {ICON_MAP[dest.icon] || <ListIcon />}
                    </ListItemIcon>
                    <ListItemText
                      primary={dest.label}
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        fontWeight: isActive(dest.path) ? 600 : 400,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        ))
      )}

      {/* Spacer */}
      <Box sx={{ flexGrow: 1 }} />

      {/* Footer Profile Card */}
      <Box sx={{ px: 2, pb: 1 }}>
        <Box
          sx={{
            borderRadius: '12px',
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              cursor: 'pointer',
              '&:hover': { backgroundColor: colors.stateHover },
            }}
            onClick={() => setProfileExpanded(prev => !prev)}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                backgroundColor: theme.palette.action.hover,
                color: theme.palette.text.primary,
                fontSize: '0.8rem',
                fontWeight: 600,
              }}
            >
              {userInitials}
            </Avatar>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  color: theme.palette.text.primary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {userName}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem' }}
              >
                {userRole}
              </Typography>
            </Box>
            {profileExpanded ? (
              <ChevronUpIcon sx={{ color: theme.palette.text.secondary, fontSize: 20 }} />
            ) : (
              <ChevronDownIcon sx={{ color: theme.palette.text.secondary, fontSize: 20 }} />
            )}
          </Box>

          <Collapse in={profileExpanded}>
            <Divider sx={{ borderColor: theme.palette.divider }} />
            <Box sx={{ p: 1 }}>
              <Button
                fullWidth
                size="small"
                onClick={() => { handleNavClick('/account'); setProfileExpanded(false); }}
                sx={{
                  color: theme.palette.text.primary,
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  px: 2,
                  '&:hover': { backgroundColor: colors.stateHover },
                }}
                startIcon={<SettingsIcon sx={{ fontSize: 18 }} />}
              >
                My Account
              </Button>
              <Button
                fullWidth
                size="small"
                onClick={handleSignOut}
                sx={{
                  color: theme.palette.text.secondary,
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  px: 2,
                  '&:hover': { color: theme.palette.text.primary, backgroundColor: colors.stateHover },
                }}
              >
                Sign Out
              </Button>
            </Box>
          </Collapse>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={isPinned ? 'persistent' : 'temporary'}
      open={isPinned || drawerNavOpen}
      onClose={isPinned ? undefined : () => dispatch(setDrawerNavOpen(false))}
      sx={{
        width: isPinned ? DRAWER_WIDTH : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: theme.palette.background.paper,
          borderRight: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary,
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default NavDrawer;
